import { nameValuePropType } from '@deltek/specification-client/core/Models/nameValuePropType'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Tooltip } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import Popper, { PopperProps } from '@material-ui/core/Popper'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import AddIcon from '@material-ui/icons/Add'
import { Delete } from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    selectLabel: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    text: {
        fontSize: pxToRem(14),
    },
    root: {
        '& legend span': {
            fontSize: '1rem',
        },
        '& .MuiAutocomplete-tag': {
            fontSize: pxToRem(14),
        },
        '& .MuiChip-deleteIconSmall': {
            width: pxToRem(20),
        },
    },
    option: { wordBreak: 'break-word' },
    projectGroupDropdownContainer: {
        backgroundColor: 'red',
    },
    projectGroupDropdownButtonContainer: {
        background: 'linear-gradient(180deg, #C68226 0%, #BD7257 100%)',
        textAlign: 'center',
        cursor: 'pointer',
        height: pxToRem(34),
        marginTop: pxToRem(-3),
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
    },
    projectGroupDropdownButton: {
        fontSize: pxToRem(12),
        color: theme.palette.common.white,
        fontWeight: 400,
    },
    addProjectGroupButton: {
        fontSize: `${pxToRem(16)} !important`,

        marginBottom: theme.spacing(0.375),
        marginRight: theme.spacing(0.25),
        opacity: '54%',
    },
    // Delete Related styles
    removeOwnerOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    ownerName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
        minWidth: 0,
    },
    deleteIcon: {
        cursor: 'pointer',
        color: '#666666',
        outline: 'none',
        '&:focus': {
            boxShadow: '0 0 0 2px #1976d2',
        },
        '&.Mui-disabled': {
            opacity: 0.5,
            pointerEvents: 'none',
            cursor: 'not-allowed',
        }
    },
}))

export interface ProjectInputProps {
    options: Record<nameValuePropType, string>[]
    name: string
    placeholder: string | undefined
    label: string
    maxLength?: number
    required: boolean
    error: boolean
    openProjectGroupDialog?: VoidFunction
    openProjectOwnerDialog?: VoidFunction
    isAdmin?: boolean
    isOwnerDisabled?: boolean
    htmlAutoCmpltDisabled?: boolean
    disable?: boolean
    optionValue?: string | null
    openRemoveParameterDialog?: (
        parameterId: string, 
        onSuccessCallback?: () => void, 
        onCancelCallback?: () => void
    ) => void
}

const popper = (props: PopperProps) => (
    <Popper
        {...props}
        placement="bottom"
        disablePortal
        modifiers={{
            flip: {
                enabled: true,
            },
        }}
    />
)

const ProjectInput: React.FC<ProjectInputProps> = ({
    openProjectGroupDialog,
    openProjectOwnerDialog,
    isAdmin,
    isOwnerDisabled,
    disable,
    options,
    name,
    label,
    placeholder,
    maxLength,
    required,
    error,
    htmlAutoCmpltDisabled,
    optionValue,
    openRemoveParameterDialog,
    ...props
}) => {
    const classes = useStyles()
    const autocompleteRef = React.useRef<HTMLDivElement | null>(null)
    const inputRef = React.useRef<HTMLInputElement | null>(null)

    const customProjectGroupPopper = (props: PopperProps) => (
        <Popper {...props} placement="bottom" disablePortal>
            {props.children}
            <div
                className={classes.projectGroupDropdownButtonContainer}
                onMouseDown={openProjectGroupDialog}
            >
                <Button className={classes.projectGroupDropdownButton}>
                    <AddIcon className={classes.addProjectGroupButton} /> Add
                    New Project Group
                </Button>
            </div>
        </Popper>
    )

    const customOwnerPopper = (props: PopperProps) => (
        <Popper {...props} placement="bottom" disablePortal>
            {props.children}
            <div
                className={classes.projectGroupDropdownButtonContainer}
                onMouseDown={openProjectOwnerDialog}
            >
                <Button className={classes.projectGroupDropdownButton}>
                    <AddIcon className={classes.addProjectGroupButton} /> Add
                    New Owner
                </Button>
            </div>
        </Popper>
    )

    /**
     * Refocuses the autocomplete input field after DOM updates
     */
    const focusAutocompleteInput = React.useCallback(() => {
        requestAnimationFrame(() => {
            autocompleteRef.current?.querySelector('input')?.focus()
        })
    }, [])

    const onSuccessRemoveParameter = React.useCallback(() => {
        focusAutocompleteInput()
    }, [focusAutocompleteInput])

    const onCancelRemoveParameter = React.useCallback(() => {
        focusAutocompleteInput()
    }, [focusAutocompleteInput])

    const renderRemoveOwnerOption = React.useCallback((option: Record<nameValuePropType, string>) => {
        const optionName = option.name
        const optionId = option.value
        return (
            <Tooltip title={optionName}>
                <div className={classes.removeOwnerOption}>
                    <span className={classes.ownerName}>
                        {optionName}
                    </span>
                    <Delete
                        data-testid={`delete-owner-${optionName}`}
                        onClick={ (e) => {
                            if(disable) return
                            e.stopPropagation()
                            openRemoveParameterDialog?.(optionId, onSuccessRemoveParameter, onCancelRemoveParameter)
                        }}
                        className={clsx(classes.deleteIcon, disable && 'Mui-disabled')}
                        aria-label={`Remove owner ${optionName}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                openRemoveParameterDialog?.(optionId, onSuccessRemoveParameter, onCancelRemoveParameter)
                            }
                        }}
                    />
                </div>
            </Tooltip>
        )
    }, [classes, disable, onCancelRemoveParameter, openRemoveParameterDialog, onSuccessRemoveParameter])

    const showDeleteOwnerButton : boolean = React.useMemo(() => {
        return label === 'Owner' && !!isAdmin
    }, [label, isAdmin])

    React.useEffect(() => {
        if (name === 'projectGroup') {
            inputRef.current?.focus()
        }
    }, [name])

    return (
        <Autocomplete
            ref={autocompleteRef}
            data-testid={`autocomplete-${name}`}
            PopperComponent={
                name === 'projectGroup' && isAdmin
                    ? customProjectGroupPopper
                    : openProjectOwnerDialog
                    ? customOwnerPopper
                    : popper
            }
            freeSolo={options.length === 0 && name !== 'Owner'}
            noOptionsText=""
            options={options}
            renderOption={(option) => (
                showDeleteOwnerButton 
                ? renderRemoveOwnerOption(option)
                : <span>{option.name}</span>
            )}
            getOptionLabel={(option: Record<nameValuePropType, string>) =>
                option.name
            }
            getOptionSelected={(option, item) => {
                return (
                    option.value === item.value || optionValue === 'Not Listed'
                )
            }}
            PaperComponent={(props) => (
                <Paper
                    {...props}
                    className={clsx(props.className, classes.text)}
                />
            )}
            disabled={(!isOwnerDisabled && name === 'Owner') || disable}
            renderInput={(params) => (
                <TextField
                    inputRef={inputRef}
                    {...params}
                    onMouseDown={
                        options.length === 0 && label === 'Owner'
                            ? openProjectOwnerDialog
                            : undefined
                    }
                    required={required}
                    size="small"
                    fullWidth={true}
                    label={label}
                    name={htmlAutoCmpltDisabled ? '' : name}
                    placeholder={placeholder}
                    variant="outlined"
                    error={error}
                    inputProps={{
                        ...params.inputProps,
                        maxLength,
                        'aria-autocomplete': 'none',
                        autoComplete: htmlAutoCmpltDisabled
                            ? 'do-not-autofill'
                            : 'on',
                    }}
                    InputLabelProps={{
                        ...params.InputLabelProps,
                        className: clsx(classes.text, classes.selectLabel),
                    }}
                    InputProps={{
                        ...params.InputProps,
                        classes: {
                            input: classes.text,
                            root: classes.root,
                        },
                    }}
                />
            )}
            {...props}
        />
    )
}
export default ProjectInput
